// Employee-kosten actuals (FO-033) + bestellingen (FO-048 P2).
//
// "Kosten op medewerker" = werkelijk geboekte kosten (los van het parametrische
// `EmployeeCostProfile`/kostprijs): InkoopFactuurRegel (employeeId+kostensoort) +
// OpleidingDeelname-kosten + Boetes. De Bestellingen-weergave deelt diezelfde
// bron (geen dubbeltelling) en voegt de uitrusting-bestellingen toe.
//
// Financieel gevoelig → serverside gegate (ADMIN/HR/management) voor het org-brede
// kosten-overzicht en andermans data. Uitsluitend backoffice: de eerdere portal
// eigen-inzage ("Mijn kosten", FO-116-B) is bewust verwijderd - een medewerker
// ziet de op hem geboekte kosten niet in de app. Bedragen in EUR (number aan de
// view-rand, Decimal in de service).

import { z } from 'zod'
import { KOSTENDRAGER_DOELTYPE, type KostendragerDoeltype } from './kostendrager'

// ─── Kostensoort (dynamisch) ─────────────────────────────────────────────────
//
// De aggregatie groepeert generiek over álle kostensoort-codes uit de stamtabel
// (facturatie kan er toevoegen — NOOIT een hardcoded subset filteren) plus de
// synthetische soort 'BOETE'. Labels zijn een hulp: onbekende keys worden
// gehumaniseerd zodat een nieuw toegevoegde kostensoort meteen netjes toont.

export const KOSTEN_BOETE_SOORT = 'BOETE'

const KOSTEN_SOORT_LABELS: Record<string, string> = {
  OPLEIDING: 'Opleiding',
  ELEKTRONICA: 'Elektronica',
  GEREEDSCHAP: 'Gereedschap',
  KLEDING: 'Kleding',
  OVERIG: 'Overig',
  DECLARATIE: 'Declaratie',
  VEILIGHEID: 'Veiligheid / PBM',
  BOETE: 'Boete',
  ABONNEMENT: 'Abonnement',
}

export function kostenSoortLabel(soort: string): string {
  const known = KOSTEN_SOORT_LABELS[soort]
  if (known) return known
  const lower = soort.toLowerCase().replace(/_/g, ' ')
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

// FO-335 (batch-39) — 'UITGIFTE' = de vijfde kostenbron: waarde van uitgegeven
// middelen (uniek exemplaar / bulk / dossier-item / kleding-bestelling).
export type KostenBron = 'INKOOP' | 'OPLEIDING' | 'BOETE' | 'UITRUSTING' | 'ABONNEMENT' | 'UITGIFTE'

export const KOSTEN_BRON_LABELS: Record<KostenBron, string> = {
  INKOOP: 'Inkoopfactuur',
  OPLEIDING: 'Opleiding',
  BOETE: 'Boete',
  UITRUSTING: 'Uitrusting-bestelling',
  ABONNEMENT: 'Abonnement',
  UITGIFTE: 'Uitgifte',
}

// ─── Kosten-tab (per medewerker) ─────────────────────────────────────────────

export interface KostenRegelDTO {
  /** Stabiele regel-id voor React-keys (bron-prefix + bron-id). */
  id: string
  datum: string
  soort: string
  soortLabel: string
  bron: KostenBron
  bronLabel: string
  /** Id van de bron voor doorklik (factuurId / opleidingId / boeteId). */
  bronId: string | null
  omschrijving: string
  aantal: number | null
  bedrag: number
  // FO-335 (batch-39) — alleen gezet op uitgifte-regels; optioneel + undefined
  // op alle bestaande bronnen zodat hun JSON byte-gelijk blijft (regressie-eis).
  /** Werkvoorraad "waarde ontbreekt": regel telt zichtbaar mee zonder bedrag. */
  bedragOnbekend?: boolean
  /** Backfill met onbekende uitgiftedatum: geboekt op registratiedatum. */
  datumGeschat?: boolean
  /** Doorklik naar de medewerker van de regel (uitgifte ↔ middel, beide kanten). */
  uitgifteEmployeeId?: string
}

export interface KostenSoortTotaalDTO {
  soort: string
  soortLabel: string
  aantal: number
  bedrag: number
}

// ─── batch-41 (FO-365) — stand "Huidige uitrusting" (bezitswaarde) ───────────
//
// STAND, geen stroom: de actuele waarde van wat de medewerker nú in bezit
// heeft, gewaardeerd volgens de FO-335-regels (uitgifte-snapshot waar die
// bestaat, anders actuele inkoopprijs met "geschat"-markering). Telt NOOIT op
// bij de maandkosten (`totaal`/`perSoort`) — de UI toont het blok visueel
// gescheiden met een uitleg-regel. Geen factuur-dedupe: de vraag is "wat staat
// er uit", niet "wat kostte het".

export interface HuidigeUitrustingSoortDTO {
  soort: string
  soortLabel: string
  /** Aantal items in bezit van deze soort (incl. items zonder waarde). */
  aantal: number
  /** Som van de bekende waarden (items zonder waarde tellen hier niet mee). */
  waarde: number
  /** Aantal items zonder waarde ("waarde ontbreekt"-werkvoorraad) binnen de soort. */
  aantalZonderWaarde: number
  /** True wanneer een deel tegen de actuele inkoopprijs is geschat (geen uitgifte-snapshot). */
  deelsGeschat: boolean
}

export interface HuidigeUitrustingDTO {
  totaalAantal: number
  totaalWaarde: number
  perSoort: HuidigeUitrustingSoortDTO[]
  /** Items die meetellen zonder bedrag (werkvoorraad "waarde ontbreekt"). */
  aantalZonderWaarde: number
  /** True wanneer een deel van de waarde geschat is (actuele prijs i.p.v. snapshot). */
  deelsGeschat: boolean
  /** Registratie-afwijkingen (bv. meer retour dan uitgifte, geklemd op 0). */
  controleregels: string[]
  /** Uit dienst mét openstaand bezit: de retourplicht loopt (badge + taken-link). */
  uitDienstRetourOpen: boolean
}

export interface EmployeeKostenDTO {
  employeeId: string
  van: string | null
  tot: string | null
  totaal: number
  perSoort: KostenSoortTotaalDTO[]
  regels: KostenRegelDTO[]
  /**
   * batch-41 (FO-365) — stand "Huidige uitrusting". Los van `totaal`/`perSoort`
   * (stand vs stroom; telt nooit op). Alleen aanwezig achter `canViewCostPrices`
   * (de hele route is daar al mee ge-gate).
   */
  huidigeUitrusting?: HuidigeUitrustingDTO
  // FO-339 (batch-39) — informatieve sectie "Voertuigkosten als bestuurder":
  // buiten `totaal`/`perSoort`/de trends. Undefined op bestaande callers zodat
  // hun JSON byte-gelijk blijft (regressie-eis).
  voertuigContext?: VoertuigKostenAlsBestuurderDTO
}

// ─── FO-339 — voertuigkosten waar de medewerker de context-bestuurder is ──────
//
// Informatief: de kost telt bij het VOERTUIG (financiële drager), maar is bij de
// bestuurder van die datum zichtbaar. Nooit in een medewerker-totaal/-trend; de
// runtime-assert (`assertContextBuitenTotaal`) bewaakt dat.

export interface VoertuigKostenAlsBestuurderRegelDTO {
  /** Stabiele id met `ctx:`-prefix zodat een context-regel nooit voor een
   *  tellende kostenregel kan doorgaan. */
  id: string
  datum: string
  voertuigId: string
  kenteken: string
  soort: string
  soortLabel: string
  omschrijving: string
  /** Informatief bedrag (excl. btw) — telt NIET mee bij de medewerker. */
  bedrag: number
  factuurId: string | null
  factuurNr: string | null
  /** Startpunt voor "verhaal op bestuurder" (de echte drager-wissel/splitsing). */
  toewijzingId: string
}

export interface VoertuigKostenAlsBestuurderDTO {
  /** Marker: dit blok is informatief en staat buiten alle medewerker-totalen. */
  informatief: true
  regels: VoertuigKostenAlsBestuurderRegelDTO[]
  /** Grijs subtotaal, BUITEN het hoofdtotaal van de medewerker. */
  subtotaal: number
}

/**
 * Runtime-invariant (FO-339 §2.2): context-regels tellen NOOIT mee in een
 * hoofdtotaal. Werpt in twee gevallen: (a) het hoofdtotaal wijkt af van de som
 * van de tellende hoofdregels (een context-bedrag zou zijn ingeslopen), of
 * (b) een `ctx:`-geprefixte context-regel-id komt voor tussen de hoofdregels.
 * Aan te roepen in de DTO-bouw (employee-kosten-route) vóór verzenden.
 */
export function assertContextBuitenTotaal(
  hoofdtotaal: number,
  hoofdRegels: { id: string; bedrag: number }[],
  context: VoertuigKostenAlsBestuurderDTO | null | undefined,
): void {
  const som = Math.round(hoofdRegels.reduce((s, r) => s + r.bedrag, 0) * 100) / 100
  if (Math.round(hoofdtotaal * 100) / 100 !== som) {
    throw new Error(
      `FO-339 invariant geschonden: hoofdtotaal (${hoofdtotaal}) ≠ som van de tellende regels (${som}) - een context-bedrag telt mee.`,
    )
  }
  if (context && hoofdRegels.some((r) => r.id.startsWith('ctx:'))) {
    throw new Error('FO-339 invariant geschonden: een context-regel (ctx:) staat tussen de tellende hoofdregels.')
  }
}

// ─── Trends (per medewerker, met functie-/teambenchmark) ─────────────────────

export const TREND_GRANULARITEITEN = ['MAAND', 'KWARTAAL', 'JAAR'] as const
export type TrendGranulariteit = (typeof TREND_GRANULARITEITEN)[number]

export interface TrendPeriodeDTO {
  /** Sorteerbare sleutel: '2026-06' | '2026-Q2' | '2026'. */
  periode: string
  label: string
  totaal: number
  perSoort: Record<string, number>
}

export interface EmployeeKostenTrendDTO {
  granulariteit: TrendGranulariteit
  soorten: { soort: string; soortLabel: string }[]
  periodes: TrendPeriodeDTO[]
  /** Functie-/teambenchmark: gemiddeld totaal per periode over de functiegenoten. */
  benchmark: {
    functie: string | null
    aantalMedewerkers: number
    periodes: { periode: string; gemTotaal: number }[]
  } | null
}

// ─── Organisatiebreed kostenoverzicht (met filters) ──────────────────────────

export interface OrgKostenRegelDTO {
  /**
   * FO-320 — generieke subject-id: employee-id (MEDEWERKER), voertuig-id
   * (VOERTUIG) of drager-id (DRAGER). De veldnamen blijven `employee*` voor
   * backward-compat met de bestaande FO-147-overzicht-UI.
   */
  employeeId: string
  employeeNaam: string
  /** MEDEWERKER: functie; VOERTUIG: kenteken; DRAGER: dragerType (als subtitel). */
  functie: string | null
  entiteitId: string | null
  entiteitNaam: string | null
  totaal: number
  perSoort: Record<string, number>
  /**
   * batch-41 (FO-365) — kolom "Uitrusting (huidig)": actuele bezitswaarde
   * (stand, los van `totaal` — telt nooit op). Alleen op de MEDEWERKER-dimensie.
   */
  uitrustingHuidig?: number
  /** Aantal bezit-items zonder waarde (werkvoorraad) achter de stand-kolom. */
  uitrustingHuidigZonderWaarde?: number
}

export interface OrgKostenOverzichtDTO {
  van: string | null
  tot: string | null
  /**
   * FO-320 — dragertype-dimensie: MEDEWERKER (default, ongewijzigd t.o.v. FO-147),
   * VOERTUIG of DRAGER. Bepaalt wat de rijen voorstellen.
   */
  dragertype: KostendragerDoeltype
  soorten: { soort: string; soortLabel: string }[]
  regels: OrgKostenRegelDTO[]
  totalen: {
    totaal: number
    perSoort: Record<string, number>
    /** batch-41 (FO-365) — som van de individuele standen (alleen MEDEWERKER). */
    uitrustingHuidig?: number
  }
}

// ─── Bestellingen (FO-048 P2 — zelfde bron als FO-033, geen dubbeltelling) ────

export type BestellingBron = 'UITRUSTING' | 'INKOOP' | 'OPLEIDING'

export const BESTELLING_BRON_LABELS: Record<BestellingBron, string> = {
  UITRUSTING: 'Uitrusting-bestelling',
  INKOOP: 'Inkooptoerekening',
  OPLEIDING: 'Opleiding',
}

export interface BestellingRegelDTO {
  id: string
  datum: string
  bron: BestellingBron
  bronLabel: string
  categorie: string
  /**
   * FO-097 — leverancier-naam, alleen ondubbelzinnig op de inkoop-bron
   * (`InkoopFactuur.leverancierId`); `null` voor uitrusting/opleiding (die hebben
   * per regel geen enkele leverancier).
   */
  leverancier: string | null
  artikel: string
  aantal: number | null
  kosten: number
  status: string | null
  bronId: string | null
}

export interface EmployeeBestellingenDTO {
  employeeId: string
  totaal: number
  perCategorie: { categorie: string; aantal: number; kosten: number }[]
  regels: BestellingRegelDTO[]
}

export interface OrgBestellingenRegelDTO extends BestellingRegelDTO {
  employeeId: string
  employeeNaam: string
}

export const BESTELLING_GROEPERINGEN = ['GEEN', 'CATEGORIE', 'MEDEWERKER', 'ARTIKEL'] as const
export type BestellingGroepering = (typeof BESTELLING_GROEPERINGEN)[number]

export interface OrgBestellingenGroepDTO {
  sleutel: string
  label: string
  aantal: number
  kosten: number
}

export interface OrgBestellingenDTO {
  regels: OrgBestellingenRegelDTO[]
  totaal: number
  groepering: BestellingGroepering
  groepen: OrgBestellingenGroepDTO[]
}

// ─── Gedeelde filter-helpers (query-params) ──────────────────────────────────

const isoDateOpt = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum')
  .optional()

/**
 * Filter-contract voor het organisatiebrede kosten-overzicht (FO-033 + FO-098).
 * Periode (van/tot, inclusief) + soort (dynamische kostensoort-key) + de
 * scope-filters BV/afdeling/medewerker/leverancier. Eén bron voor de API-route
 * en (waar van toepassing) de client. Alle velden optioneel; alleen aanwezige
 * filters worden server-side toegepast.
 */
export const KostenPeriodeFilterSchema = z.object({
  van: isoDateOpt,
  tot: isoDateOpt,
  soort: z.string().min(1).optional(),
  entiteitId: z.string().min(1).optional(),
  departmentId: z.string().min(1).optional(),
  employeeId: z.string().min(1).optional(),
  leverancierId: z.string().min(1).optional(),
  // FO-320 — dragertype-dimensie + drager-filter. `dragertype` bepaalt of het
  // overzicht medewerkers (default), voertuigen of karren/koffers toont;
  // voertuigId/gereedschapsDragerId scopen naar één drager (voorgefilterde weergave).
  // batch-65 (FO-483): alleen de interne dragers (klant-toerekening heeft een eigen overzicht).
  dragertype: z.enum(KOSTENDRAGER_DOELTYPE).optional(),
  voertuigId: z.string().min(1).optional(),
  gereedschapsDragerId: z.string().min(1).optional(),
})
export type KostenPeriodeFilter = z.infer<typeof KostenPeriodeFilterSchema>
